import React, { useEffect, useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import {
  getPendingRequests,
  getApprovalsHistory,
  approveRequest,
  rejectRequest,
  bulkApproveRequests,
  bulkRejectRequests,
  getCurrentUser,
} from "../api/approver";
import "../styles/ApproverPage.css";

// ============================================================================
// CONSTANTS
// ============================================================================
// FIXED: Match backend PR status values. Backend sets "APPROVED" after L1 approval.
const L1_DONE_STATUSES = ["approved", "first approval done"];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "item_asc", label: "Item A-Z" },
  { value: "item_desc", label: "Item Z-A" },
  // { value: "qty_desc", label: "Qty High-Low" },
  // { value: "qty_asc", label: "Qty Low-High" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "l1 approved", label: "L1 Approved" },
  { value: "first approval done", label: "First Approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const DATE_RANGE_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

// ============================================================================
// HELPERS
// ============================================================================
const dedupeByPR = (arr) => {
  const map = new Map();
  (arr || []).forEach((item) => {
    const key = item?.purchase_request?.id ?? item.id;
    if (key != null && !map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

const parseDate = (dateStr) => new Date(dateStr);

const isInDateRange = (dateStr, range) => {
  const date = parseDate(dateStr);
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (range) {
    case "today": return date >= startOfDay;
    case "week": {
      const startOfWeek = new Date(startOfDay);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      return date >= startOfWeek;
    }
    case "month":
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    default: return true;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = parseDate(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatRelativeDate = (dateStr) => {
  if (!dateStr) return "";
  const date = parseDate(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const getStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "pending") return { dot: "#f59e0b", bg: "#fffbeb", text: "#92400e" };
  if (s === "l1 approved") return { dot: "#a9ae50", bg: "#f8f9f0", text: "#575a2e" };
  if (s === "first approval done") return { dot: "#a9ae50", bg: "#f8f9f0", text: "#575a2e" };
  if (s === "approved") return { dot: "#10b981", bg: "#ecfdf5", text: "#065f46" };
  if (s === "rejected") return { dot: "#ef4444", bg: "#fef2f2", text: "#991b1b" };
  return { dot: "#9ca3af", bg: "#f3f4f6", text: "#4b5563" };
};

const getInitials = (name) => (name?.[0] || "?").toUpperCase();

// ============================================================================
// FILTER: Level-based visibility
// ============================================================================
const filterPendingForUser = (arr, user) => {
  if (!user) return [];
  const level = user.approver_level;
  return dedupeByPR(arr).filter((appr) => {
    if (appr.status !== "pending") return false;
    if (appr.approval_level !== level) return false;
    // FIXED: Use case-insensitive matching for PR status
    const prStatus = (appr.purchase_request?.status ?? "").toLowerCase();
    if (level === 1) return prStatus === "pending";
    if (level === 2) return L1_DONE_STATUSES.includes(prStatus);
    return false;
  });
};

// FIXED: Added "ordered" and "finance" to terminal states
const filterHistory = (arr) => {
  const terminal = ["delivered", "collected", "ordered", "finance"];
  return dedupeByPR(arr).filter((appr) => {
    if (!["approved", "rejected"].includes(appr.status?.toLowerCase())) return false;
    const prStatus = (appr.purchase_request?.status ?? "").toLowerCase();
    return !terminal.includes(prStatus);
  });
};

// ============================================================================
// COMPONENT: Stat Card
// ============================================================================
const StatCard = ({ value, label, accent, delay }) => (
  <div className="stat-card" style={{ animationDelay: `${delay}ms` }}>
    <div className="stat-top">
      <span className="stat-number">{value}</span>
      <div className="stat-indicator" style={{ backgroundColor: accent }} />
    </div>
    <span className="stat-label">{label}</span>
  </div>
);

// ============================================================================
// COMPONENT: Request Card (for flat view)
// ============================================================================
const RequestCard = ({ appr, activeTab, onApprove, onReject }) => {
  const pr = appr.purchase_request || {};
  const status = getStatusStyle(pr.status);
  const decision = getStatusStyle(appr.status);

  return (
    <div className="request-card">
      <div className="card-top">
        <div className="card-id">
          <span className="id-label">PR</span>
          <span className="id-num">#{pr.id}</span>
        </div>
        <div className="card-status" style={{ background: status.bg, color: status.text }}>
          <span className="status-dot" style={{ background: status.dot }} />
          {pr.status || "—"}
        </div>
      </div>

      <div className="card-body">
        <h3 className="card-title">{pr.item || "—"}</h3>
        <p className="card-desc">{pr.description || "No description provided"}</p>

        <div className="card-meta">
          <div className="meta-group">
            <span className="meta-label">Quantity</span>
            <span className="meta-value">{pr.quantity} <span className="meta-unit">{pr.units}</span></span>
          </div>
          <div className="meta-group">
            <span className="meta-label">Cost Centre</span>
            <span className="meta-value">{pr.cost_centre || "—"}</span>
          </div>
          <div className="meta-group">
            <span className="meta-label">Stock</span>
            <span className="meta-value">{pr.stock_balance ?? "—"}</span>
          </div>
          <div className="meta-group">
            <span className="meta-label">Lot</span>
            <span className="meta-value">{pr.lot_number || "—"}</span>
          </div>
        </div>

        {pr.remarks && (
          <div className="card-remarks">
            <span className="remarks-label">Remarks</span>
            <p>{pr.remarks}</p>
          </div>
        )}
      </div>

      <div className="card-footer">
        <div className="card-requester">
          <div className="req-avatar" style={{ background: `linear-gradient(135deg, #a92f6f, #c44a8a)` }}>
            {getInitials(pr.requester_name)}
          </div>
          <div className="req-info">
            <span className="req-name">{pr.requester_name || "—"}</span>
            <span className="req-date">{formatDate(appr.created_at)} • {formatRelativeDate(appr.created_at)}</span>
          </div>
        </div>

        {activeTab === "pending" ? (
          <div className="card-actions">
            <button className="btn-approve" onClick={() => onApprove(appr.id)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Approve
            </button>
            <button className="btn-reject" onClick={() => onReject(appr.id)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Reject
            </button>
          </div>
        ) : (
          <div className="card-history">
            <span className="h-approver">{appr.approver_name || "—"}</span>
            <span className="h-decision" style={{ background: decision.bg, color: decision.text }}>
              {appr.status === "approved" ? "Approved" : "Rejected"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ApproverPage = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [history, setHistory] = useState([]);
  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [expandedLots, setExpandedLots] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const [sortBy, setSortBy] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [lotFilter, setLotFilter] = useState("all");
  const [requesterFilter, setRequesterFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grouped");

  // Fetch User
  useEffect(() => {
    (async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch {
        Swal.fire("Error", "Failed to fetch user data", "error");
      }
    })();
  }, []);

  // Fetch Pending
  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const raw = await getPendingRequests();
        setPendingApprovals(filterPendingForUser(raw, user));
      } catch {
        Swal.fire("Error", "Failed to fetch pending approvals", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // Fetch History (lazy)
  useEffect(() => {
    if (activeTab !== "history" || historyLoaded) return;
    (async () => {
      setLoading(true);
      try {
        const raw = await getApprovalsHistory();
        setHistory(filterHistory(raw));
        setHistoryLoaded(true);
      } catch {
        Swal.fire("Error", "Failed to fetch history", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTab, historyLoaded]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, statusFilter, dateRange, lotFilter, requesterFilter, activeTab, viewMode]);

  const allLots = useMemo(() => {
    const source = activeTab === "pending" ? pendingApprovals : history;
    const lots = [...new Set(source.map(a => a.purchase_request?.lot_number || "Unassigned"))];
    return lots.sort();
  }, [pendingApprovals, history, activeTab]);

  const allRequesters = useMemo(() => {
    const source = activeTab === "pending" ? pendingApprovals : history;
    const req = [...new Set(source.map(a => a.purchase_request?.requester_name || "Unknown"))];
    return req.sort();
  }, [pendingApprovals, history, activeTab]);

  const filteredData = useMemo(() => {
    let source = activeTab === "pending" ? pendingApprovals : history;
    const q = search.trim().toLowerCase();
    if (q) {
      source = source.filter((a) => {
        const pr = a.purchase_request || {};
        return (
          (pr.item ?? "").toLowerCase().includes(q) ||
          (pr.description ?? "").toLowerCase().includes(q) ||
          (pr.requester_name ?? "").toLowerCase().includes(q) ||
          (pr.lot_number ?? "").toLowerCase().includes(q) ||
          (pr.cost_centre ?? "").toLowerCase().includes(q) ||
          (pr.remarks ?? "").toLowerCase().includes(q)
        );
      });
    }
    if (activeTab === "history" && statusFilter !== "all") {
      source = source.filter(a => (a.status ?? "").toLowerCase() === statusFilter);
    }
    if (dateRange !== "all") {
      source = source.filter(a => isInDateRange(a.created_at, dateRange));
    }
    if (lotFilter !== "all") {
      source = source.filter(a => (a.purchase_request?.lot_number || "Unassigned") === lotFilter);
    }
    if (requesterFilter !== "all") {
      source = source.filter(a => (a.purchase_request?.requester_name || "Unknown") === requesterFilter);
    }
    source = [...source].sort((a, b) => {
      const prA = a.purchase_request || {};
      const prB = b.purchase_request || {};
      switch (sortBy) {
        case "newest": return parseDate(b.created_at) - parseDate(a.created_at);
        case "oldest": return parseDate(a.created_at) - parseDate(b.created_at);
        case "item_asc": return (prA.item || "").localeCompare(prB.item || "");
        case "item_desc": return (prB.item || "").localeCompare(prA.item || "");
        case "qty_desc": return (prB.quantity || 0) - (prA.quantity || 0);
        case "qty_asc": return (prA.quantity || 0) - (prB.quantity || 0);
        default: return 0;
      }
    });
    return source;
  }, [activeTab, pendingApprovals, history, search, sortBy, statusFilter, dateRange, lotFilter, requesterFilter]);

  const groupedData = useMemo(() => {
    if (viewMode === "flat") return null;
    return filteredData.reduce((acc, appr) => {
      const lot = appr.purchase_request?.lot_number || "Unassigned";
      if (!acc[lot]) acc[lot] = [];
      acc[lot].push(appr);
      return acc;
    }, {});
  }, [filteredData, viewMode]);

  const lotKeys = groupedData ? Object.keys(groupedData) : [];
  const totalPages = viewMode === "grouped"
    ? Math.max(1, Math.ceil(lotKeys.length / itemsPerPage))
    : Math.max(1, Math.ceil(filteredData.length / itemsPerPage));

  const currentLots = viewMode === "grouped"
    ? lotKeys.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : null;

  const currentFlatItems = viewMode === "flat"
    ? filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : null;

  const stats = useMemo(() => {
    const totalPending = pendingApprovals.length;
    const totalHistory = history.length;
    const approvedCount = history.filter(a => a.status?.toLowerCase() === "approved").length;
    const rejectedCount = history.filter(a => a.status?.toLowerCase() === "rejected").length;
    const uniqueLots = new Set(pendingApprovals.map(a => a.purchase_request?.lot_number)).size;
    return { totalPending, totalHistory, approvedCount, rejectedCount, uniqueLots };
  }, [pendingApprovals, history]);

  const toggleLotExpand = useCallback((lot) => {
    setExpandedLots((prev) => ({ ...prev, [lot]: !prev[lot] }));
  }, []);

  const toggleSelectItem = useCallback((lot, id) => {
    setSelectedItems((prev) => {
      const current = prev[lot] || [];
      return {
        ...prev,
        [lot]: current.includes(id) ? current.filter((i) => i !== id) : [...current, id],
      };
    });
  }, []);

  const toggleSelectAllInLot = useCallback((lot, items) => {
    setSelectedItems((prev) => {
      const allIds = items.map((i) => i.id);
      const allSelected = (prev[lot] || []).length === allIds.length && allIds.length > 0;
      return { ...prev, [lot]: allSelected ? [] : allIds };
    });
  }, []);

  const removePending = useCallback((ids) => {
    setPendingApprovals((prev) => prev.filter((p) => !ids.includes(p.id)));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearch("");
    setSortBy("newest");
    setStatusFilter("all");
    setDateRange("all");
    setLotFilter("all");
    setRequesterFilter("all");
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (sortBy !== "newest") count++;
    if (statusFilter !== "all") count++;
    if (dateRange !== "all") count++;
    if (lotFilter !== "all") count++;
    if (requesterFilter !== "all") count++;
    return count;
  }, [sortBy, statusFilter, dateRange, lotFilter, requesterFilter]);

  // Approve
  const handleApprove = async (id) => {
    const { value: comments, isConfirmed } = await Swal.fire({
      title: "Approve Request",
      html: `<div style="text-align:left;margin-bottom:12px;">
        <label style="display:block;margin-bottom:6px;font-weight:600;color:#374151;">Approval Note (Optional)</label>
        <textarea id="swal-input" class="swal2-textarea" placeholder="Add any notes about this approval..."
          style="width:100%;min-height:80px;border-radius:8px;border:1px solid #e5e7eb;padding:10px;font-family:inherit;"></textarea>
      </div>`,
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#a9ae50",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
      preConfirm: () => document.getElementById("swal-input")?.value || "",
    });
    if (!isConfirmed) return;

    setProcessing(true);
    try {
      await approveRequest(id, {}, comments || "");
      removePending([id]);
      Swal.fire({ icon: "success", title: "Approved", text: "Request approved successfully.",
        confirmButtonColor: "#a9ae50", timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Approval failed.", "error");
    } finally { setProcessing(false); }
  };

  // Reject
  const handleReject = async (id) => {
    const { value: remarks, isConfirmed } = await Swal.fire({
      title: "Reject Request",
      html: `<div style="text-align:left;margin-bottom:12px;">
        <label style="display:block;margin-bottom:6px;font-weight:600;color:#a92f6f;">Rejection Reason <span style="color:#dc2626;">*</span></label>
        <textarea id="swal-reason" class="swal2-textarea" placeholder="Please provide a reason for rejection..."
          style="width:100%;min-height:80px;border-radius:8px;border:1px solid #e5e7eb;padding:10px;font-family:inherit;"></textarea>
      </div>`,
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#a92f6f",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
      preConfirm: () => {
        const val = document.getElementById("swal-reason")?.value?.trim();
        if (!val) Swal.showValidationMessage("Please provide a rejection reason");
        return val;
      },
    });
    if (!isConfirmed) return;

    setProcessing(true);
    try {
      await rejectRequest(id, remarks);
      removePending([id]);
      Swal.fire({ icon: "success", title: "Rejected", text: "Request rejected.",
        confirmButtonColor: "#a92f6f", timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Rejection failed.", "error");
    } finally { setProcessing(false); }
  };

  // Bulk Action
  const handleBulkAction = async (lotNumber, actionType) => {
    const selectedIds = selectedItems[lotNumber] || [];
    if (!selectedIds.length) {
      return Swal.fire("No Selection", "Please select at least one item.", "warning");
    }

    const isApprove = actionType === "approved";
    const { value: comments, isConfirmed } = await Swal.fire({
      title: `${isApprove ? "Approve" : "Reject"} ${selectedIds.length} Item${selectedIds.length > 1 ? "s" : ""}`,
      html: `<div style="text-align:left;margin-bottom:12px;">
        <p style="margin-bottom:10px;color:#6b7280;">You are about to ${isApprove ? "approve" : "reject"} <strong>${selectedIds.length}</strong> selected item${selectedIds.length > 1 ? "s" : ""}.</p>
        <label style="display:block;margin-bottom:6px;font-weight:600;color:${isApprove ? "#a9ae50" : "#a92f6f"};">${isApprove ? "Approval Note (Optional)" : "Rejection Reason *"}</label>
        <textarea id="swal-batch" class="swal2-textarea" placeholder="${isApprove ? "Add a note..." : "Enter rejection reason..."}"
          style="width:100%;min-height:80px;border-radius:8px;border:1px solid #e5e7eb;padding:10px;font-family:inherit;"></textarea>
      </div>`,
      showCancelButton: true,
      confirmButtonText: isApprove ? "Approve Selected" : "Reject Selected",
      confirmButtonColor: isApprove ? "#a9ae50" : "#a92f6f",
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9ca3af",
      preConfirm: () => {
        const val = document.getElementById("swal-batch")?.value || "";
        if (!isApprove && !val.trim()) Swal.showValidationMessage("Please provide a rejection reason");
        return val;
      },
    });
    if (!isConfirmed) return;

    setProcessing(true);
    try {
      if (isApprove) await bulkApproveRequests(selectedIds, comments || "");
      else await bulkRejectRequests(selectedIds, comments || "");

      removePending(selectedIds);
      setSelectedItems((prev) => ({ ...prev, [lotNumber]: [] }));
      Swal.fire({ icon: "success", title: isApprove ? "Approved" : "Rejected",
        text: `${selectedIds.length} item${selectedIds.length > 1 ? "s" : ""} ${isApprove ? "approved" : "rejected"} successfully.`,
        confirmButtonColor: isApprove ? "#a9ae50" : "#a92f6f", timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.detail || "Bulk action failed.", "error");
    } finally { setProcessing(false); }
  };

  const levelLabel = user?.approver_level === 1 ? "L1" : user?.approver_level === 2 ? "L2" : "";
  const levelColor = user?.approver_level === 1 ? "#a92f6f" : "#a9ae50";

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="approver-page">
      {/* ===== TOP NAV ===== */}
      <nav className="top-nav">
        <div className="nav-brand">
          <div className="brand-mark">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="7" fill="#a92f6f" />
              <path d="M8 14L12 18L20 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="brand-text">
            <span className="brand-name">Approval Center</span>
            <span className="brand-tag">Workflow</span>
          </div>
        </div>

        <div className="nav-user">
          {user && (
            <div className="user-chip">
              <div className="user-avatar" style={{ background: levelColor }}>
                {getInitials(user.first_name || user.username)}
              </div>
              <div className="user-text">
                <span className="user-name">{user.first_name || user.username}</span>
                <span className="user-level" style={{ color: levelColor }}>{levelLabel} Approver</span>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ===== STATS ===== */}
      <div className="stats-strip">
        <StatCard value={stats.totalPending} label="Pending" accent="#a92f6f" delay={0} />
        <StatCard value={stats.totalHistory} label="History" accent="#6b7280" delay={80} />
        <StatCard value={stats.approvedCount} label="Approved" accent="#a9ae50" delay={160} />
        <StatCard value={stats.rejectedCount} label="Rejected" accent="#ef4444" delay={240} />
        <StatCard value={stats.uniqueLots} label="Lots" accent="#0ea5e9" delay={320} />
      </div>

      {/* ===== TABS ===== */}
      <div className="tab-strip">
        <div className="tab-set">
          <button
            className={`tab-item ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            <span className="tab-dot" style={{ background: activeTab === "pending" ? "#fff" : "#a92f6f" }} />
            <span>Pending</span>
            {stats.totalPending > 0 && <span className="tab-count">{stats.totalPending}</span>}
          </button>
          <button
            className={`tab-item ${activeTab === "history" ? "active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            <span className="tab-dot" style={{ background: activeTab === "history" ? "#fff" : "#9ca3af" }} />
            <span>History</span>
            {stats.totalHistory > 0 && <span className="tab-count alt">{stats.totalHistory}</span>}
          </button>
        </div>
      </div>

      {/* ===== BANNER ===== */}
      {activeTab === "pending" && user?.approver_level === 2 && (
        <div className="info-strip l2">
          <span className="strip-icon">🔐</span>
          <div className="strip-body">
            <strong>Level 2 Review</strong>
            <span>Items below passed L1 approval. Once you action an item, it disappears from all other L2 queues.</span>
          </div>
        </div>
      )}
      {activeTab === "pending" && user?.approver_level === 1 && (
        <div className="info-strip l1">
          <span className="strip-icon">⚡</span>
          <div className="strip-body">
            <strong>Level 1 Review</strong>
            <span>Your approval moves items to L2. Approved items immediately vanish from other L1 queues.</span>
          </div>
        </div>
      )}

      {/* ===== TOOLBAR ===== */}
      <div className="toolbar">
        <div className="search-box">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search by item, requester, lot, cost centre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="search-x" onClick={() => setSearch("")}>×</button>}
        </div>

        <div className="toolbar-right">
          <div className="view-toggle">
            <button className={viewMode === "grouped" ? "on" : ""} onClick={() => setViewMode("grouped")} title="Group by Lot">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
            </button>
            <button className={viewMode === "flat" ? "on" : ""} onClick={() => setViewMode("flat")} title="Card View">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
            </button>
          </div>

          <button className={`filter-trigger ${showFilters ? "open" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`} onClick={() => setShowFilters(!showFilters)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <span>Filter</span>
            {activeFilterCount > 0 && <span className="filter-dot">{activeFilterCount}</span>}
          </button>

          <div className="size-picker">
            <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
              {[12, 24, 36, 48].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ===== FILTER PANEL ===== */}
      {showFilters && (
        <div className="filter-sheet">
          <div className="sheet-grid">
            <div className="sheet-block">
              <label>Sort By</label>
              <div className="chip-row">
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.value} className={sortBy === opt.value ? "chip active" : "chip"} onClick={() => setSortBy(opt.value)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === "history" && (
              <div className="sheet-block">
                <label>Status</label>
                <div className="chip-row">
                  {STATUS_OPTIONS.map(opt => (
                    <button key={opt.value} className={statusFilter === opt.value ? "chip active" : "chip"} onClick={() => setStatusFilter(opt.value)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="sheet-block">
              <label>Date Range</label>
              <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                {DATE_RANGE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            <div className="sheet-block">
              <label>Lot</label>
              <select value={lotFilter} onChange={(e) => setLotFilter(e.target.value)}>
                <option value="all">All Lots</option>
                {allLots.map(lot => <option key={lot} value={lot}>{lot}</option>)}
              </select>
            </div>

            <div className="sheet-block">
              <label>Requester</label>
              <select value={requesterFilter} onChange={(e) => setRequesterFilter(e.target.value)}>
                <option value="all">All</option>
                {allRequesters.map(req => <option key={req} value={req}>{req}</option>)}
              </select>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="sheet-footer">
              <button className="clear-btn" onClick={clearAllFilters}>Clear All</button>
              <span className="tally">{filteredData.length} results</span>
            </div>
          )}
        </div>
      )}

      {/* ===== RESULTS COUNT ===== */}
      <div className="results-line">
        <span>
          <strong>{filteredData.length}</strong> {activeTab === "pending" ? "pending" : "history"} items
          {viewMode === "grouped" && <em> — grouped by lot</em>}
        </span>
      </div>

      {/* ===== CONTENT ===== */}
      {loading ? (
        <div className="loading-wrap">
          <div className="spinner-dual">
            <div className="spin-ring" />
            <div className="spin-ring delay" />
          </div>
          <p>Loading {activeTab === "pending" ? "approvals" : "history"}...</p>
        </div>
      ) : (
        <div className="content-wrap">
          {filteredData.length === 0 ? (
            <div className="empty-wrap">
              <div className="empty-graphic">
                <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="40" fill="#f3f4f6" />
                  <circle cx="50" cy="50" r="28" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 4" />
                  <text x="50" y="58" textAnchor="middle" fontSize="32">{activeTab === "pending" ? "✓" : "∅"}</text>
                </svg>
              </div>
              <h3>{activeTab === "pending" ? "All Clear" : "No Results"}</h3>
              <p>{activeTab === "pending" ? "No pending items match your filters." : "No history items found."}</p>
              {activeFilterCount > 0 && <button className="empty-reset" onClick={clearAllFilters}>Reset Filters</button>}
            </div>
          ) : viewMode === "grouped" ? (
            /* ===== GROUPED BY LOT ===== */
            <div className="lot-stack">
              {currentLots.map((lot) => {
                const items = groupedData[lot] || [];
                const lotSelectedIds = selectedItems[lot] || [];
                const allSelected = items.length > 0 && lotSelectedIds.length === items.length;
                const someSelected = lotSelectedIds.length > 0 && !allSelected;
                const isExpanded = expandedLots[lot] || false; // DEFAULT COLLAPSED

                const totalQty = items.reduce((sum, i) => sum + (i.purchase_request?.quantity || 0), 0);
                const uniqueReqs = [...new Set(items.map(i => i.purchase_request?.requester_name))];

                return (
                  <div key={lot} className={`lot-panel ${isExpanded ? "open" : ""}`}>
                    <div className="lot-header" onClick={() => toggleLotExpand(lot)}>
                      <div className="lot-left">
                        <div className="lot-chevron">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                        <div className="lot-badge">{lot}</div>
                        <div className="lot-summary">
                          <span>{items.length} items</span>
                          <span className="sep">•</span>
                          <span>Qty {totalQty}</span>
                          <span className="sep">•</span>
                          <span>{uniqueReqs.length} requester{uniqueReqs.length !== 1 ? "s" : ""}</span>
                        </div>
                      </div>

                      <div className="lot-right">
                        {activeTab === "pending" && (
                          <>
                            <label className="lot-select" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                onChange={() => toggleSelectAllInLot(lot, items)}
                              />
                              <span>{lotSelectedIds.length > 0 ? `${lotSelectedIds.length} selected` : "Select"}</span>
                            </label>
                            <div className="lot-actions">
                              <button
                                className="lot-btn approve"
                                disabled={lotSelectedIds.length === 0}
                                onClick={(e) => { e.stopPropagation(); handleBulkAction(lot, "approved"); }}
                              >
                                Approve
                              </button>
                              <button
                                className="lot-btn reject"
                                disabled={lotSelectedIds.length === 0}
                                onClick={(e) => { e.stopPropagation(); handleBulkAction(lot, "rejected"); }}
                              >
                                Reject
                              </button>
                            </div>
                          </>
                        )}
                        <span className="lot-num">{items.length}</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="lot-body">
                        <div className="item-table">
                          <div className="table-header">
                            {activeTab === "pending" && <div className="th-check" />}
                            <div className="th-item">Item</div>
                            <div className="th-desc">Description</div>
                            <div className="th-qty">Qty</div>
                            <div className="th-unit">Unit</div>
                            <div className="th-cost">Cost Centre</div>
                            <div className="th-stock">Stock</div>
                            <div className="th-requester">Requester</div>
                            <div className="th-date">Date</div>
                            <div className="th-status">Status</div>
                            {activeTab === "history" && <div className="th-decision">Decision</div>}
                            {activeTab === "pending" && <div className="th-action">Action</div>}
                          </div>

                          {items.map((appr) => {
                            const pr = appr.purchase_request || {};
                            const isSelected = lotSelectedIds.includes(appr.id);
                            const status = getStatusStyle(pr.status);
                            const decision = getStatusStyle(appr.status);

                            return (
                              <div key={appr.id} className={`table-row ${isSelected ? "selected" : ""}`}>
                                {activeTab === "pending" && (
                                  <div className="td-check">
                                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelectItem(lot, appr.id)} />
                                  </div>
                                )}
                                <div className="td-item">
                                  <span className="td-name">{pr.item}</span>
                                  <span className="td-pr">PR#{pr.id}</span>
                                </div>
                                <div className="td-desc">{pr.description || "—"}</div>
                                <div className="td-qty">{pr.quantity}</div>
                                <div className="td-unit">{pr.units || "—"}</div>
                                <div className="td-cost">{pr.cost_centre || "—"}</div>
                                <div className="td-stock">{pr.stock_balance ?? "—"}</div>
                                <div className="td-requester">
                                  <div className="td-avatar" style={{ background: `linear-gradient(135deg, #a92f6f, #c44a8a)` }}>
                                    {getInitials(pr.requester_name)}
                                  </div>
                                  <span>{pr.requester_name || "—"}</span>
                                </div>
                                <div className="td-date">
                                  <span>{formatDate(appr.created_at)}</span>
                                  <span className="td-rel">{formatRelativeDate(appr.created_at)}</span>
                                </div>
                                <div className="td-status">
                                  <span className="status-tag" style={{ background: status.bg, color: status.text }}>
                                    <span className="tag-dot" style={{ background: status.dot }} />
                                    {pr.status || "—"}
                                  </span>
                                </div>
                                {activeTab === "history" && (
                                  <div className="td-decision">
                                    <span className="decision-tag" style={{ background: decision.bg, color: decision.text }}>
                                      {appr.status === "approved" ? "Approved" : "Rejected"}
                                    </span>
                                  </div>
                                )}
                                {activeTab === "pending" && (
                                  <div className="td-action">
                                    <button className="act-btn approve" onClick={() => handleApprove(appr.id)} title="Approve">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    </button>
                                    <button className="act-btn reject" onClick={() => handleReject(appr.id)} title="Reject">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* ===== FLAT CARD VIEW ===== */
            <div className="card-grid">
              {currentFlatItems.map((appr) => (
                <RequestCard
                  key={appr.id}
                  appr={appr}
                  activeTab={activeTab}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== PAGINATION ===== */}
      {totalPages > 1 && (
        <div className="page-strip">
          <span className="page-label">Page {currentPage} of {totalPages}</span>
          <div className="page-nums">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              if (pageNum < 1 || pageNum > totalPages) return null;
              return (
                <button key={pageNum} className={currentPage === pageNum ? "active" : ""} onClick={() => setCurrentPage(pageNum)}>
                  {pageNum}
                </button>
              );
            })}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        </div>
      )}

      {/* ===== PROCESSING ===== */}
      {processing && (
        <div className="process-mask">
          <div className="process-box">
            <div className="process-spin">
              <div className="spin-track" />
              <div className="spin-head" />
            </div>
            <p>Processing...</p>
            <span>Please wait</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproverPage;